// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import { Component, Element, Event, h, Prop, State, Watch } from '@stencil/core'

import type { Attributes } from '../../helpers'
import { inheritAriaAttributes } from '../../helpers'
import { getIconLibrary } from './library'
import requestIcon from './request'

let parser: DOMParser | undefined

/**
 * @experimental
 */
@Component({
  tag: 'qds-icon',
  shadow: true,
  styleUrl: 'icon.css',
})
export class Icon implements ComponentInterface {
  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly name!: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly library: string = 'default'

  /**
   * Emitted when the icon has loaded.
   */
  @Event({ eventName: 'qdsLoad', cancelable: false })
  private readonly loadEmitter!: EventEmitter<void>

  /**
   * Emitted when the icon fails to load due to an error.
   */
  @Event({ eventName: 'qdsError', cancelable: false })
  private readonly errorEmitter!: EventEmitter<void>

  @Element() private readonly element!: HTMLQdsIconElement

  @State() private svg = ''

  private inheritedAttributes: Attributes = {}

  @Watch('name')
  @Watch('library')
  private async setIcon(): Promise<void> {
    if (!this.name) return

    const library = getIconLibrary(this.library)
    if (!library) return

    const url = library.resolver(this.name)
    if (!url) {
      this.svg = ''
      return
    }

    try {
      const request = await requestIcon(url)
      if (!request.ok) {
        this.svg = ''
        this.errorEmitter.emit()
        return
      }

      // Create an instance of the DOM parser. We do it here instead of at the
      // top-level to support SSR while maintaining a single parser instance
      // for optimal performance.
      parser ||= new DOMParser()
      const document = parser.parseFromString(request.data, 'text/html')
      const svgElement = document.body.querySelector('svg')
      if (svgElement === null) {
        this.svg = ''
        this.errorEmitter.emit()
      } else {
        for (const [key, value] of Object.entries(this.inheritedAttributes))
          if (value !== undefined) svgElement.setAttribute(key, value)
        this.svg = svgElement.outerHTML
        this.loadEmitter.emit()
      }
    } catch {
      this.errorEmitter.emit()
    }
  }

  public async componentWillLoad(): Promise<void> {
    this.inheritedAttributes = inheritAriaAttributes(this.element)
    await this.setIcon()
  }

  public render() {
    return (
      <div class="qds-container">
        <span class="qds-icon-wrapper" innerHTML={this.svg} />
        <div class="qds-click-target" />
      </div>
    )
  }
}
